import type React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import dayjs from 'dayjs';
import { Copy, TrashIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// moved to core/components
import type { SharedFolderToken } from '@/core/api/services/archives';

type ShareArchiveLinksTableProps = {
  links: SharedFolderToken[];
  fallbackFolderId?: string;
  onRevoke: (token: string) => void;
};

export const ShareArchiveLinksTable: React.FC<ShareArchiveLinksTableProps> = ({
  links,
  fallbackFolderId,
  onRevoke,
}) => {
  const { t } = useTranslation();

  return (
    <Table className="my-5 block max-h-[50vh] w-full table-auto bg-transparent">
      <TableHeader className="h-11 max-sm:hidden">
        <TableRow className="w-full justify-between">
          <TableHead className="text-center text-text-secondary">Link</TableHead>
          <TableHead className="text-center text-text-secondary">{t('created-at')}</TableHead>
          <TableHead className="text-center text-text-secondary">{t('expires-at')}</TableHead>
          <TableHead className="text-center text-text-secondary"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {links.map((l) => {
          const folderId = l.folderId ?? fallbackFolderId ?? '';
          const url = `${window.location.origin}/shared/folder/${folderId}?token=${l.token}`;
          return (
            <TableRow key={l.token} className="items-center text-primary-dark">
              <TableCell className="max-w-40 truncate py-5 text-center">
                <a className="no-underline" href={url} target="__blank">
                  {url}
                </a>
              </TableCell>
              <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                {dayjs(l.createdAt).format('DD.MM.YYYY')}
              </TableCell>
              <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                {dayjs(l.expiryDate).format('DD.MM.YYYY')}
              </TableCell>
              <TableCell>
                <div className="hidden w-auto min-w-32 items-center justify-end gap-1 py-5 text-center lg:grid lg:grid-cols-2 xl:flex">
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(url);
                      toast.success(t('copied-url'));
                    }}
                    variant="outline"
                    className="border-2"
                  >
                    <Copy aria-hidden />
                    <span className="sr-only">{t('copy')}</span>
                  </Button>
                  <Button
                    className="max-xl:max-w-[60px]"
                    onClick={() => onRevoke(l.token)}
                    variant="destructive"
                  >
                    <TrashIcon aria-hidden />
                    <span className="sr-only"> {t('delete')}</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
